<template>
  <div class="pmp">
    <div class="pmp-tiles" role="group" :aria-label="$t('paymentPicker.aria')">
      <button
        v-for="item in methods"
        :key="item.value"
        type="button"
        class="pmp-tile"
        :class="{
          'pmp-tile--on': item.value === value,
          'pmp-tile--blocked': !!missingFor(item.value),
        }"
        :aria-pressed="item.value === value ? 'true' : 'false'"
        :disabled="!!busy"
        @click="$emit('select', item.value)"
      >
        <span class="pmp-tile__badge" aria-hidden="true">
          <v-progress-circular
            v-if="busy === item.value"
            indeterminate
            size="18"
            width="2"
            color="#e8276f"
          />
          <v-icon v-else-if="item.value === value" size="20" color="#e8276f"
            >mdi-check-circle</v-icon
          >
        </span>

        <span class="pmp-tile__icon" aria-hidden="true">
          <v-icon size="22">{{ item.icon }}</v-icon>
        </span>
        <span class="pmp-tile__label">{{ item.label }}</span>
        <span class="pmp-tile__trait">{{ item.trait }}</span>
        <span v-if="missingFor(item.value)" class="pmp-tile__missing">
          {{ missingFor(item.value) }}
        </span>
      </button>
    </div>

    <transition name="pmp-fade" mode="out-in">
      <div
        :key="value"
        class="pmp-month"
        :style="{ '--c': accent }"
        aria-live="polite"
      >
        <span class="pmp-month__stripe" aria-hidden="true"></span>
        <p class="pmp-month__title">{{ $t("paymentPicker.monthTitle") }}</p>
        <ol class="pmp-steps">
          <li v-for="step in steps" :key="step.when" class="pmp-step">
            <span class="pmp-step__when">{{ step.when }}</span>
            <span class="pmp-step__what">{{ step.what }}</span>
          </li>
        </ol>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_MISSING,
} from "@/shared/paymentMethods";

const ACCENTS = {
  credit_card: "#e8276f",
  pix: "#00a379",
  boleto: "#231a2e",
};

export default {
  name: "PaymentMethodPicker",

  props: {
    // Forma escolhida hoje: "credit_card" | "pix" | "boleto".
    value: {
      type: String,
      default: "credit_card",
    },
    // `[{ method, available, reason }]` vindo de GET /customers/profile.
    options: {
      type: Array,
      default: () => [],
    },
    card: {
      type: Object,
      default: () => ({}),
    },
    // Forma sendo gravada agora (trava os botões e mostra o spinner nela).
    busy: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      methods: PAYMENT_METHODS,
    };
  },

  computed: {
    accent() {
      return ACCENTS[this.value] || ACCENTS.credit_card;
    },
    // Como a mensalidade aparece em Faturas com a forma escolhida. Os prazos
    // espelham o worker de cobrança (pitch-english-billing, config.py):
    // Pix/boleto emitidos 5 dias antes do vencimento e válidos até 23:59 do
    // dia; vencidos sem pagamento, são reemitidos. O cartão é cobrado no
    // vencimento com o descritor "PITCH". Mudou lá, muda aqui.
    steps() {
      const t = (key, values) => this.$t(`paymentPicker.${key}`, values);
      const generated = { when: t("endOfMonth"), what: t("generated") };

      if (this.value === "pix") {
        return [
          generated,
          { when: t("fiveDays"), what: t("pixCode") },
          { when: t("untilDue"), what: t("pixPay") },
          { when: t("ifLate"), what: t("pixLate") },
        ];
      }

      if (this.value === "boleto") {
        return [
          generated,
          { when: t("fiveDays"), what: t("boletoCode") },
          { when: t("untilDue"), what: t("boletoPay") },
          { when: t("ifLate"), what: t("boletoLate") },
        ];
      }

      const card =
        this.card && this.card.ready && this.card.last4
          ? t("cardEnding", {
              brand: this.card.brand ? ` ${this.card.brand}` : "",
              last4: this.card.last4,
            })
          : t("cardDefault");

      return [
        generated,
        { when: t("onDue"), what: t("cardCharge", { card }) },
        { when: t("after"), what: t("cardAfter") },
      ];
    },
  },

  methods: {
    // A forma escolhida nunca mostra pendência: se ela está gravada, a API já
    // aceitou os requisitos.
    missingFor(method) {
      if (method === this.value) return "";
      const option = this.options.find((item) => item.method === method);
      if (!option || option.available) return "";
      return PAYMENT_METHOD_MISSING[option.reason] || "";
    },
  },
};
</script>

<style scoped>
.pmp {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-pink, #e8276f);
  --ember: var(--dk-ember-deep, #b4560f);
}

.pmp-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.pmp-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 18px 18px 16px;
  border: 1.5px solid var(--line);
  border-radius: 18px;
  background: var(--dk-surface, #fff);
  color: var(--ink);
  text-align: left;
  transition: border-color 0.18s ease, background 0.18s ease,
    box-shadow 0.18s ease;
}
.pmp-tile:hover:not(:disabled) {
  border-color: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.2);
  box-shadow: 0 14px 30px -24px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.6);
}
.pmp-tile:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.pmp-tile:disabled {
  cursor: progress;
}
.pmp-tile--on {
  border-color: var(--accent);
  background: rgba(232, 39, 111, 0.05);
}
.pmp-tile--on:hover:not(:disabled) {
  border-color: var(--accent);
}

.pmp-tile__badge {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  min-height: 20px;
}

.pmp-tile__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: var(--paper);
}
.pmp-tile__icon .v-icon {
  color: var(--ink);
}
.pmp-tile--on .pmp-tile__icon .v-icon {
  color: var(--accent);
}

.pmp-tile__label {
  font-size: 0.95rem;
  font-weight: 700;
}
.pmp-tile__trait {
  font-size: 0.8rem;
  color: var(--muted);
}
.pmp-tile__missing {
  margin-top: 6px;
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ember);
}

/* ---------- Como fica seu mês ---------- */
.pmp-month {
  --c: var(--dk-pink, #e8276f);
  position: relative;
  margin-top: 16px;
  padding: 16px 20px 16px 24px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
}
.pmp-month__stripe {
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: var(--c);
}
.pmp-month__title {
  margin: 0 0 10px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

.pmp-steps {
  margin: 0;
  padding: 0;
  list-style: none;
}
.pmp-step {
  position: relative;
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 12px;
  padding-left: 18px;
  font-size: 0.86rem;
  line-height: 1.45;
}
.pmp-step + .pmp-step {
  margin-top: 10px;
}
/* Os dois passos acontecem em sequência: um ponto por passo, ligados por um
   fio na cor da forma escolhida. */
.pmp-step::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.45em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c);
}
/* O fio sai do ponto deste passo e desce até o ponto do próximo (10px de
   margem + o mesmo recuo do ponto), qualquer que seja a altura do texto. */
.pmp-step:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 3.5px;
  top: calc(0.45em + 8px);
  bottom: calc(-10px - 0.45em);
  width: 1px;
  background: color-mix(in srgb, var(--c) 45%, var(--dk-surface, #fff));
}
.pmp-step__when {
  font-weight: 700;
  color: var(--ink);
}
.pmp-step__what {
  color: var(--muted);
}

.pmp-fade-enter-active,
.pmp-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.pmp-fade-enter,
.pmp-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@media (max-width: 760px) {
  .pmp-tiles {
    grid-template-columns: 1fr;
  }
  /* Empilhados, os botões viram linhas: ícone à esquerda, texto ao lado. */
  .pmp-tile {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 2px;
    align-items: center;
    padding: 14px 44px 14px 14px;
  }
  .pmp-tile__icon {
    grid-row: 1 / span 3;
    align-self: start;
    margin-bottom: 0;
  }
  .pmp-tile__label,
  .pmp-tile__trait,
  .pmp-tile__missing {
    grid-column: 2;
  }
  .pmp-tile__missing {
    margin-top: 2px;
  }
  .pmp-step {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pmp-tile,
  .pmp-fade-enter-active,
  .pmp-fade-leave-active {
    transition: none;
  }
}
</style>
